/**
 * MANAGEMENT (14) data layer — the ONLY place the super-admin console talks to the server. Every path +
 * capability below is code-verified against
 * server/src/modules/management/{onboarding,support,subscriptions,paramtypes}.routes.js. Server data
 * lives ONLY in TanStack Query; mutations invalidate the affected platform keys.
 *
 * CROSS-LAB, NOT lab-scoped. Unlike the lab cockpit (`qk.scoped(labId, …)`), the management plane spans
 * EVERY lab — the keys are rooted under a single `["platform", "management", …]` namespace so a lab switch
 * never invalidates them and one operator session reads the whole estate. The server gate
 * (`authenticatePlatform`) opens no tenant context; there is no `labId` to scope by.
 *
 * Endpoints consumed (all under /api/v1, all on the `platformRoute` chain — NO public surface):
 *   GET    /management/labs                              management.lab.read
 *   GET    /management/labs/:id                          management.lab.read
 *   POST   /management/labs                              management.lab.onboard
 *   POST   /management/labs/:id/statut                   management.lab.onboard
 *   GET    /management/subscriptions                     management.lab.read
 *   GET    /management/subscriptions/:laboratoireId      management.lab.read
 *   PUT    /management/subscriptions/:laboratoireId      management.subscription.manage
 *   DELETE /management/subscriptions/:laboratoireId      management.subscription.manage
 *   GET    /management/support/tickets                   management.support.manage
 *   GET    /management/support/unread                    management.support.manage
 *   GET    /management/paramtypes                        management.paramtype.manage
 *
 * THE CONVENTION (the screen agents follow this verbatim):
 *   - the api client is the shared `api` from "@/api" (api.get/post/put/patch/delete);
 *   - reads are `useApiQuery(managementKeys.<x>(...), "/management/...", { enabled, staleTime })`,
 *     where `enabled` = `useIsPlatformAdmin() && online` (gate before the data branches);
 *   - writes are `useApiMutation((vars) => api.<verb>("/management/...", vars), { invalidate: [...] })`;
 *   - 404/501 (a cloud-only module not mounted on this install) → `isManagementAbsent` → the clean
 *     "not available here" state (the queryClient treats 404/501 as terminal — never retried).
 */
import { ApiError, api, qk, useApiMutation, useApiQuery } from "@/api";
import type {
  LabDirectoryFilters,
  LabDirectoryRow,
  Page,
  ParamtypeFilters,
  ParamtypeRow,
  SubscriptionFilters,
  SubscriptionRow,
  SupportFilters,
  SupportTicketPage,
  SupportUnread,
} from "./types";

export { MANAGEMENT_CAPS } from "./caps";

/** Strip undefined/null/empty so the cache key is stable and the server gets a clean query. */
export function cleanQuery(filters: object): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "") out[k] = v as string | number | boolean;
  }
  return out;
}

/**
 * The platform-scoped query-key factory. Rooted under `["platform", "management", …]` (NOT `qk.scoped`,
 * which is lab-tenant-scoped) so the cross-lab estate is one coherent cache. `qk.scoped(null, …)` is
 * reused only as a stable array-builder; the leading `"_"` lab slot is inert on this plane.
 */
export const managementKeys = {
  root: ["platform", "management"] as const,
  labs: (filters: LabDirectoryFilters = {}) =>
    ["platform", "management", "labs", filters] as const,
  lab: (id: string) => ["platform", "management", "labs", id] as const,
  subscriptions: (filters: SubscriptionFilters = {}) =>
    ["platform", "management", "subscriptions", filters] as const,
  subscription: (laboratoireId: string) =>
    ["platform", "management", "subscriptions", laboratoireId] as const,
  supportTickets: (filters: SupportFilters = {}) =>
    ["platform", "management", "support", "tickets", filters] as const,
  supportUnread: () => ["platform", "management", "support", "unread"] as const,
  paramtypes: (filters: ParamtypeFilters = {}) =>
    ["platform", "management", "paramtypes", filters] as const,
};

// (intentional re-export so a screen can build an ad-hoc lab-scoped key without a second import)
export { qk };

/**
 * True when an ApiError means a management route is not mounted on THIS install — the management module is
 * deployClass:cloud, so on a local lab box its routes 404 (or a not-yet-implemented surface returns 501).
 * Drives the "console not available on this install" state instead of a hard error.
 */
export function isManagementAbsent(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    !error.isForbidden &&
    !error.isUnauthenticated &&
    (error.httpStatus === 404 || error.httpStatus === 501 || error.code === "NOT_FOUND")
  );
}

// ── 1. Lab directory (onboarding) ──────────────────────────────────────────────────
/** GET /management/labs — the cross-lab directory, filtered + server-paginated. */
export function useLabs(filters: LabDirectoryFilters, enabled: boolean) {
  return useApiQuery<Page<LabDirectoryRow>>(managementKeys.labs(filters), "/management/labs", {
    query: cleanQuery(filters),
    enabled,
    staleTime: 30_000,
  });
}

/** GET /management/labs/:id — a single lab drill-down. */
export function useLab(id: string, enabled: boolean) {
  return useApiQuery<LabDirectoryRow>(managementKeys.lab(id), `/management/labs/${id}`, {
    enabled,
    staleTime: 30_000,
  });
}

// ── 2. Subscriptions directory ─────────────────────────────────────────────────────
/** GET /management/subscriptions — every lab + its current entitlement projection, paginated. */
export function useSubscriptions(filters: SubscriptionFilters, enabled: boolean) {
  return useApiQuery<Page<SubscriptionRow>>(
    managementKeys.subscriptions(filters),
    "/management/subscriptions",
    { query: cleanQuery(filters), enabled, staleTime: 30_000 },
  );
}

// ── 3. Support / helpdesk ──────────────────────────────────────────────────────────
/** GET /management/support/tickets — the cross-lab triage inbox (page + per-status counts + open total). */
export function useSupportTickets(filters: SupportFilters, enabled: boolean) {
  return useApiQuery<SupportTicketPage>(
    managementKeys.supportTickets(filters),
    "/management/support/tickets",
    { query: cleanQuery(filters), enabled, staleTime: 15_000 },
  );
}

/** GET /management/support/unread — the cross-lab unread badge (PHI-free count). Pollable. */
export function useSupportUnread(enabled: boolean) {
  return useApiQuery<SupportUnread>(managementKeys.supportUnread(), "/management/support/unread", {
    enabled,
    staleTime: 15_000,
  });
}

// ── 4. Param-types (global catalogue) ──────────────────────────────────────────────
/** GET /management/paramtypes — the global analysis-parameter-type catalogue, paginated. */
export function useParamtypes(filters: ParamtypeFilters, enabled: boolean) {
  return useApiQuery<Page<ParamtypeRow>>(managementKeys.paramtypes(filters), "/management/paramtypes", {
    query: cleanQuery(filters),
    enabled,
    staleTime: 30_000,
  });
}

// ── Mutations — the WRITE conventions the section agents follow (reference impls, shell-owned subset) ──
// The full provision/triage/changePlan/paramtype-CRUD mutations are owned by the section agents; the
// shell ships the canonical PATTERN (verb + path + invalidate the affected platform key). Example:
//
//   export function useProvisionLab() {
//     return useApiMutation<ProvisionResult, ProvisionVars>(
//       (vars) => api.post("/management/labs", vars),
//       { invalidate: [managementKeys.root] },     // refresh the whole management estate
//     );
//   }
//
// Re-exported here so a section agent can wire one without re-importing the core.
export { api, useApiMutation, useApiQuery };
